import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import VideoPlayerCard from "../components/video/VideoPlayerCard";
import AIRationaleCard from "../components/video/AIRationaleCard";
import BehavioralEventsCard from "../components/video/BehavioralEventsCard";
import VideoDetailsCard from "../components/video/VideoDetailsCard";
import AIScoresCard from "../components/video/AIScoresCard";
import AppShell from "../components/AppShell";
import type {  VideoRecord } from "../types/videoTypes";
import { getVideoById, submitVideoReview } from "../services/videos";
import {
  formatDuration,
  titleize,
} from "../utils/time";
import QualityCard from "../components/video/QualityCard";
import DeceptionSignalsCard from "../components/video/DeceptionSignalsCard";
import CaregiverReviewPanel from "../components/review/CaregiverReviewPanel";

function VideoDetailPage() {
  const { videoId } = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [video, setVideo] = useState<VideoRecord | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!videoId) return;

    async function loadVideo() {
      try {
        setLoading(true);
        setError("");
        const response = await getVideoById(videoId as string);
        setVideo(response);
      } catch (err) {
        console.error(err);
        setError("Failed to load video details");
        setVideo(undefined);
      } finally {
        setLoading(false);
      }
    }

    loadVideo();
  }, [videoId]);

  function seekTo(seconds: number) {
    if (!videoRef.current) return;
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
    videoRef.current.currentTime = seconds;
    // void videoRef.current.play();
  }

  const formatDecision = (decision?: string) => decision
    ? decision
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

  if (loading) {
    return (
      <AppShell title="Loading video" subtitle="Fetching details...">
        <div className="panel">
          <div className="loader-container">
            <div className="loading"></div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!video || error) {
    return (
      <AppShell title="Video not found" subtitle="">
        <div className="panel empty-state">
          <p>Unable to find this video.</p>
          <Link className="text-button" to="/videos">
            Return to list
          </Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>

      {/* GRID LAYOUT */}
      <div className="review-page-layout">

        {/* LEFT SIDE */}
        <div className="review-page-main">
          <VideoPlayerCard
            videoId={video.videoId}
            videoSrc={video.videoSrc}
            // deceptionFlag={video.aiAssessment.deceptionFlag}
            videoRef={videoRef}
          />
          <AIRationaleCard
            rationale={video.aiAssessment.rationale}
          />
          <BehavioralEventsCard
            events={video.behavioralEvents}
            onSeek={seekTo}
          />
  
         <CaregiverReviewPanel
            onAccept={async (review) => {
              if (!video) {
                return;
              }

              await submitVideoReview({
                aiProcessingId:video.aiProcessingId,
                reviewerId:"4454",
                preparation:review.preparation,
                execution:review.execution,
                completion:review.completion,
                overAll:review.overallScore,
                feedback: review.comments,
                status: "ACCEPTED",
              });
            }}

            onReject={async (review) => {
              if (!video) {
                return;
              }

              await submitVideoReview({
                aiProcessingId: video?.aiProcessingId,
                reviewerId:"454",
                preparation:review.preparation,
                execution:review.execution,
                completion:review.completion,
                overAll: review.overallScore,
                feedback: review.comments,
                status: "REJECTED",
              });
            }}
          />
        </div>

        {/* RIGHT SIDE */}
        <aside className="review-page-sidebar">
          <VideoDetailsCard
            duration={formatDuration(video.durationSec)}
            frames={video.framesAnalyzed.toLocaleString()}
            // fps={String(video.sampledFps)}
            // condition="-"
          />
          <AIScoresCard
            preparation={
              video.aiAssessment.preparationScore
            }
            execution={
              video.aiAssessment.executionScore
            }
            completion={
              video.aiAssessment.completionScore
            }
            overall={
              video.aiAssessment.overallScore
            }
          />
          <div className="ai-verdict-section">
            <span className="ai-verdict-label">
              AI VERDICT
            </span>

            <span className={`ai-verdict-pill ${video.videoQuality.final_decision === "ACCEPTED"
                  ? "ai-verdict-pill--accepted"
                  : video.videoQuality.final_decision === "REJECTED"
                  ? "ai-verdict-pill--rejected"
                  : "ai-verdict-pill--review"
              }`}
            >
              {formatDecision(video.videoQuality?.final_decision)}
            </span>
          </div>

          <QualityCard
            light={titleize(video.videoQuality.lightLevel)}
            lowLight={`${video.videoQuality.lowLightPct.toFixed(1)}%`}
            blurFrames={String(video.videoQuality.blurFrames)}
            blackouts={String(video.videoQuality.blackoutFrames)}
            // pass={
            //   video.videoQuality.qualityPass
            //     ? "Yes"
            //     : "Needs review"
            // }
          />
          <DeceptionSignalsCard
            confidence={String(
              video.deceptionSignals.deceptionConfidence
            )}
            offCameraFrames={String(
              video.deceptionSignals.zeroPersonFrames
            )}
            multiplePersonFrames={String(
              video.deceptionSignals.multiplePersonFrames
            )} 
            samePersonFrames={String(
              video.deceptionSignals.samePersonFrames
            )} 
          />
        </aside>
      </div>
    </AppShell>
  );
}

export default VideoDetailPage;